/**
 * Petites annonces (espace compétition) — panneau rendu dans l'onglet Forums.
 *
 * Cartes typées (recherche joueur / recherche équipe / défi ouvert) avec
 * actions contextuelles (Postuler, Inviter, Relever le défi, Répondre,
 * Clôturer) plutôt que des fils de discussion génériques.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { PlayerAvatar } from '../PlayerAvatar';
import { useProfile } from '../../contexts/ProfileContext';
import {
  getAnnonces, getAnnonceDetail, createAnnonce, replyAnnonce, postulerAnnonce,
  inviterAnnonce, fixerRdvAnnonce, cloturerAnnonce, getMyTeam,
  type Annonce, type MyTeam,
} from '../../lib/teamsApi';
import { BACKEND_URL } from '../../config';
import type { Session } from '../../types/app';
import {
  clearForumReplyDraft,
  forumReplyDraftKey,
  loadForumReplyDraft,
  saveForumReplyDraft,
} from '../../utils/forumReplyDrafts';

type FilterKind = '' | 'joueur' | 'equipe' | 'defi';

const KIND_LABEL: Record<string, string> = {
  joueur: 'Recherche joueur',
  equipe: 'Recherche équipe',
  defi: 'Défi ouvert',
};
const KIND_COLOR: Record<string, string> = {
  joueur: '#26c6da',
  equipe: '#7e57c2',
  defi: '#e6b800',
};

function fmtDate(v: string): string {
  try {
    const d = new Date(v);
    return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return v;
  }
}

function authorLabel(a: Annonce): string {
  if (a.author?.author_type === 'team') return a.author.team_nom || `Équipe #${a.author.team_id}`;
  return a.author?.pseudo_display || a.pseudo_display || a.pseudo;
}

function authorAvatarUrl(a: Annonce): string | undefined {
  if (a.author?.author_type === 'team' && a.author.team_nom) {
    return `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(a.author.team_nom)}?size=64`;
  }
  return undefined;
}

interface AnnoncesPanelProps {
  session: Session;
}

export default function AnnoncesPanel({ session }: AnnoncesPanelProps) {
  const { openPlayerProfile, openTeamProfile } = useProfile();
  const [filter, setFilter] = useState<FilterKind>('');
  const [items, setItems] = useState<Annonce[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [myTeam, setMyTeam] = useState<MyTeam | null>(null);
  useEffect(() => {
    if (!session.token) return;
    getMyTeam(session.token).then((d) => setMyTeam(d.team)).catch(() => {});
  }, [session.token]);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [thread, setThread] = useState<Annonce | null>(null);
  const [replies, setReplies] = useState<Annonce[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const replyDraftKeyRef = useRef<string | null>(null);
  const replyBodyDraftRef = useRef('');
  replyBodyDraftRef.current = replyBody;

  const buildReplyDraftKey = useCallback((annonceId: number | null): string | null => {
    if (annonceId == null || !session.pseudo) return null;
    return forumReplyDraftKey('competition', session.pseudo, 'annonces', annonceId);
  }, [session.pseudo]);

  const persistCurrentReplyDraft = useCallback(() => {
    const key = replyDraftKeyRef.current;
    if (!key) return;
    const body = replyBodyDraftRef.current;
    if (body.trim()) saveForumReplyDraft(key, body);
    else clearForumReplyDraft(key);
  }, []);

  const switchReplyDraft = useCallback((annonceId: number | null) => {
    persistCurrentReplyDraft();
    const nextKey = buildReplyDraftKey(annonceId);
    replyDraftKeyRef.current = nextKey;
    const loaded = nextKey ? loadForumReplyDraft(nextKey) : '';
    replyBodyDraftRef.current = loaded;
    setReplyBody(loaded);
  }, [persistCurrentReplyDraft, buildReplyDraftKey]);

  const clearReplyDraftState = useCallback(() => {
    persistCurrentReplyDraft();
    replyDraftKeyRef.current = null;
    replyBodyDraftRef.current = '';
    setReplyBody('');
  }, [persistCurrentReplyDraft]);

  useEffect(() => {
    const key = replyDraftKeyRef.current;
    if (!key) return;
    const timer = window.setTimeout(() => {
      const body = replyBodyDraftRef.current;
      if (body.trim()) saveForumReplyDraft(key, body);
      else clearForumReplyDraft(key);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [replyBody]);

  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitre, setNewTitre] = useState('');
  const [newContenu, setNewContenu] = useState('');
  const [newKind, setNewKind] = useState<'joueur' | 'defi'>('joueur');
  const [newRdv, setNewRdv] = useState('');
  const [newNbparties, setNewNbparties] = useState(4);

  const [challengeRdv, setChallengeRdv] = useState('');

  const loadList = useCallback(() => {
    setLoading(true);
    setError(null);
    getAnnonces({ annonce: filter || undefined, limit: 50 })
      .then((d) => { setItems(d.items); setTotal(d.total); })
      .catch(() => setError('Impossible de charger les annonces'))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = useCallback((id: number) => {
    switchReplyDraft(id);
    setSelectedId(id);
    setLoadingDetail(true);
    getAnnonceDetail(id)
      .then((d) => { setThread(d.thread); setReplies(d.replies); setChallengeRdv(d.thread.rdv?.slice(0, 16) || ''); })
      .catch(() => setError('Impossible de charger cette annonce'))
      .finally(() => setLoadingDetail(false));
  }, [switchReplyDraft]);

  const closeDetail = () => {
    clearReplyDraftState();
    setSelectedId(null);
    setThread(null);
    setReplies([]);
  };

  const run = async (action: () => Promise<unknown>, refreshList = true) => {
    if (!session.token) return;
    setBusy(true);
    setError(null);
    try {
      await action();
      if (selectedId != null) openDetail(selectedId);
      if (refreshList) loadList();
    } catch {
      setError('Action impossible');
    } finally {
      setBusy(false);
    }
  };

  const isStaff = Boolean(myTeam?.is_staff);
  const myPseudoLower = session.pseudo.toLowerCase();

  const isMyAnnonce = (a: Annonce | null) => {
    if (!a) return false;
    if (a.author?.author_type === 'team') return Boolean(myTeam && myTeam.id === a.author.team_id);
    return (a.author?.pseudo || a.pseudo).toLowerCase() === myPseudoLower;
  };

  return (
    <div className="forums-panel-full" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {selectedId == null ? (
        <>
          <div className="forums-col-header">
            <h2>Petites annonces</h2>
            {session.token && (
              <button
                type="button"
                className="forums-new-btn"
                onClick={() => setShowNewForm((v) => !v)}
                title="Nouvelle annonce"
              >
                <span className="forums-new-btn-plus" aria-hidden>+</span>
                <span className="forums-new-btn-label">Nouvelle annonce</span>
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, padding: '8px 16px', flexWrap: 'wrap' }}>
            {(['', 'joueur', 'equipe', 'defi'] as FilterKind[]).map((k) => (
              <button
                key={k || 'all'}
                onClick={() => setFilter(k)}
                style={{
                  fontWeight: filter === k ? 700 : 400,
                  borderRadius: 20, padding: '4px 12px',
                  border: '1px solid var(--border-color)',
                  background: filter === k ? 'var(--bg-hover)' : 'transparent',
                }}
              >
                {k ? KIND_LABEL[k] : `Toutes (${total})`}
              </button>
            ))}
          </div>

          {showNewForm && session.token && (
            <div style={{ margin: '0 16px 12px', padding: 12, border: '1px solid var(--border-color)', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {isStaff && (
                <div style={{ display: 'flex', gap: 12 }}>
                  <label>
                    <input type="radio" checked={newKind === 'joueur'} onChange={() => setNewKind('joueur')} /> Recherche de joueur
                  </label>
                  <label>
                    <input type="radio" checked={newKind === 'defi'} onChange={() => setNewKind('defi')} /> Défi ouvert
                  </label>
                </div>
              )}
              <input placeholder="Titre" value={newTitre} onChange={(e) => setNewTitre(e.target.value)} />
              <textarea placeholder="Message" rows={3} value={newContenu} onChange={(e) => setNewContenu(e.target.value)} />
              {isStaff && newKind === 'defi' && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <input type="datetime-local" value={newRdv} onChange={(e) => setNewRdv(e.target.value)} />
                  <label>Parties <input type="number" min={1} max={50} value={newNbparties} onChange={(e) => setNewNbparties(Number(e.target.value))} style={{ width: 60 }} /></label>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button disabled={busy} onClick={() => setShowNewForm(false)}>Annuler</button>
                <button
                  disabled={busy || !newTitre.trim() || !newContenu.trim()}
                  onClick={() => run(async () => {
                    await createAnnonce(session.token!, {
                      titre: newTitre.trim(), contenu: newContenu.trim(),
                      annonce: isStaff ? newKind : 'equipe',
                      rdv: isStaff && newKind === 'defi' && newRdv ? new Date(newRdv).toISOString() : undefined,
                      nbparties: isStaff && newKind === 'defi' ? newNbparties : undefined,
                    });
                    setShowNewForm(false);
                    setNewTitre('');
                    setNewContenu('');
                  })}
                >
                  Publier
                </button>
              </div>
            </div>
          )}

          <div className="forums-col-scroll" style={{ flex: 1 }}>
            {loading && <div className="forums-loading">Chargement…</div>}
            {error && <div className="compet-error" style={{ margin: 16 }}>{error}</div>}
            {!loading && items.length === 0 && (
              <div className="forums-empty-center">
                <div className="forums-empty-center-icon">📣</div>
                <div>Aucune annonce pour le moment</div>
              </div>
            )}
            {!loading && items.map((a) => (
              <button
                key={a.id}
                className="forums-thread-item forums-thread-item--inline-authors"
                onClick={() => openDetail(a.id)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10 }}
              >
                <PlayerAvatar pseudo={authorLabel(a)} size={32} photoUrl={authorAvatarUrl(a)} />
                <div className="forums-thread-info" style={{ flex: 1, textAlign: 'left' }}>
                  <div className="forums-thread-subject">
                    <span
                      style={{
                        fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                        color: KIND_COLOR[a.annonce], marginRight: 8,
                      }}
                    >
                      {KIND_LABEL[a.annonce]}
                    </span>
                    <span className="forums-thread-subject-text">{a.titre}</span>
                  </div>
                  <div className="forums-thread-meta">
                    <span>{authorLabel(a)}</span>
                    <span className="forums-thread-sep">·</span>
                    <span className="forums-thread-date">{fmtDate(a.quand)}</span>
                    {(a.reply_count ?? 0) > 0 && (
                      <>
                        <span className="forums-thread-sep">·</span>
                        <span>{a.reply_count} réponse{(a.reply_count ?? 0) > 1 ? 's' : ''}</span>
                      </>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="forums-col-header">
            <button className="forums-back-btn" onClick={closeDetail}>← Annonces</button>
          </div>
          <div className="forums-col-scroll" style={{ flex: 1, padding: 16 }}>
            {loadingDetail && <div className="forums-loading">Chargement…</div>}
            {!loadingDetail && thread && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <PlayerAvatar pseudo={authorLabel(thread)} size={40} photoUrl={authorAvatarUrl(thread)} />
                  <div>
                    <h2 style={{ margin: 0 }}>{thread.titre}</h2>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      <button
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit' }}
                        onClick={() => (thread.author?.author_type === 'team' && thread.author.team_id
                          ? openTeamProfile(thread.author.team_id)
                          : openPlayerProfile(thread.author?.pseudo || thread.pseudo))}
                      >
                        {authorLabel(thread)}
                      </button>
                      {' · '}{fmtDate(thread.quand)}
                      {thread.etat !== 'visible' && ` · ${thread.etat === 'clos' ? 'Clôturée' : thread.etat}`}
                    </div>
                  </div>
                </div>

                <p style={{ whiteSpace: 'pre-wrap' }}>{thread.contenu}</p>

                {thread.annonce === 'defi' && thread.rdv && (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
                    Créneau proposé : {fmtDate(thread.rdv)} · {thread.nbparties} partie{thread.nbparties > 1 ? 's' : ''}
                    {thread.surcontrats ? ' · Surcontrats' : ''}{thread.att_tournant ? ' · Attaquant tournant' : ''}
                  </div>
                )}

                {session.token && thread.etat === 'visible' && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {thread.annonce === 'joueur' && !myTeam && (
                      <button disabled={busy} onClick={() => run(() => postulerAnnonce(session.token!, thread.id))}>
                        Postuler
                      </button>
                    )}
                    {thread.annonce === 'equipe' && isStaff && (
                      <button disabled={busy} onClick={() => run(() => inviterAnnonce(session.token!, thread.id))}>
                        Inviter dans mon équipe
                      </button>
                    )}
                    {thread.annonce === 'defi' && isStaff && myTeam!.id !== thread.author?.team_id && (
                      <>
                        <input
                          type="datetime-local"
                          value={challengeRdv}
                          onChange={(e) => setChallengeRdv(e.target.value)}
                        />
                        <button
                          disabled={busy}
                          onClick={() => run(() => fixerRdvAnnonce(session.token!, thread.id, {
                            rdv: challengeRdv ? new Date(challengeRdv).toISOString() : undefined,
                          }))}
                        >
                          Relever le défi
                        </button>
                      </>
                    )}
                    {isMyAnnonce(thread) && (
                      <button disabled={busy} onClick={() => run(() => cloturerAnnonce(session.token!, thread.id))}>
                        Clôturer l'annonce
                      </button>
                    )}
                  </div>
                )}

                {replies.length > 1 && (
                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 12 }}>
                    {replies.slice(1).map((r) => (
                      <div key={r.id} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                        <PlayerAvatar pseudo={r.pseudo} size={28} />
                        <div>
                          <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                            {r.pseudo_display || r.pseudo} · {fmtDate(r.quand)}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{r.contenu}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {session.token && thread.etat === 'visible' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    <textarea
                      placeholder="Répondre…"
                      rows={3}
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                    />
                    <button
                      disabled={busy || !replyBody.trim()}
                      style={{ alignSelf: 'flex-end' }}
                      onClick={() => run(async () => {
                        await replyAnnonce(session.token!, thread.id, replyBody.trim());
                        if (replyDraftKeyRef.current) clearForumReplyDraft(replyDraftKeyRef.current);
                        replyBodyDraftRef.current = '';
                        setReplyBody('');
                      }, false)}
                    >
                      Répondre
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
